import { eigen_decomposition, Matrix, sort_vals } from "./matrix-math";
import THREE from "three";

const tmppt = new THREE.Vector3();

// Function to generate a line segment where endpoints do not go farther along the axis than the projection of any input point on that axis
// and passes through a given point
export function generateLineSegment(points, nStartPts, principalAxis, givenPoint) {

  //Find the farthest entry points in each direction
  let minDotEnter = points[0].dot(principalAxis);
  let maxDotEnter = minDotEnter;
  for (let i = 1; i < nStartPts; i++) {
    const proj = points[i].dot(principalAxis);
    if (proj < minDotEnter) {
      minDotEnter = proj;
    }
    if (proj > maxDotEnter) {
      maxDotEnter = proj;
    }
  }

  //Find the farthest exit points in each direction
  let minDotExit = points[nStartPts].dot(principalAxis);
  let maxDotExit = minDotExit;
  for (let i = nStartPts + 1; i < points.length; i++) {
    const proj = points[i].dot(principalAxis);
    if (proj < minDotExit) {
      minDotExit = proj;
    }
    if (proj > maxDotExit) {
      maxDotExit = proj;
    }
  }

  // Project the given point onto the principal axis
  const dotCenter = givenPoint.dot(principalAxis);

  //Pick start and end points are on
  //opposite sides of the center point if possible
  let startPtParam;
  let endPtParam;

  if (Math.abs(minDotEnter - dotCenter) > Math.abs(maxDotEnter - dotCenter)) {
    startPtParam = minDotEnter;
  } else {
    startPtParam = maxDotEnter;
  }

  if (Math.abs(startPtParam - minDotExit) > Math.abs(startPtParam - maxDotExit)) {
    endPtParam = minDotExit;
  } else {
    endPtParam = maxDotExit;
  }

  // Calculate the shift to align the segment with the given point
  // by subtracting the projection of the given point on the axis from the given point
  const shift = tmppt.copy(principalAxis).multiplyScalar(-dotCenter).add(givenPoint);

  // Shift the minimum and maximum projections to align with the given point
  const startPoint = principalAxis.clone().multiplyScalar(startPtParam).add(shift);
  const endPoint = principalAxis.clone().multiplyScalar(endPtParam).add(shift);

  return { startPoint, endPoint };
}

function clampToZero(v, baseline) {
  let relative = v * baseline;
  if (Math.abs(relative) < 1e-12)
  return 0;

  return v;
}

const A = new Matrix(3, 3);
const V = new Matrix(3, 3);
const d = [0, 0, 0];

export class MassMoments {

  constructor() {
    this.cx = 0;
    this.cy = 0;
    this.cz = 0;

    this.xx = 0;
    this.yy = 0;
    this.zz = 0;
    this.xy = 0;
    this.yz = 0;
    this.zx = 0;

    this.n = 0;
    this.w = 0;
  }

  addPoint(p, w = 1.0) {
    this.n++;
    this.w += w;

    this.cx += p.x * w;
    this.cy += p.y * w;
    this.cz += p.z * w;

    this.xx += p.x * p.x * w;
    this.yy += p.y * p.y * w;
    this.zz += p.z * p.z * w;

    this.xy += p.x * p.y * w;
    this.yz += p.y * p.z * w;
    this.zx += p.z * p.x * w;
  }

  getCovMatrix(A) {
    const scale = 1.0 / this.w;
    const comX = this.cx * scale;
    const comY = this.cy * scale;
    const comZ = this.cz * scale;

    const xx = this.xx - this.w * comX * comX;
    const yy = this.yy - this.w * comY * comY;
    const zz = this.zz - this.w * comZ * comZ;

    const xy = this.xy - this.w * comX * comY;
    const yz = this.yz - this.w * comY * comZ;
    const zx = this.zx - this.w * comZ * comX;

    const Ixx = yy + zz;
    const Iyy = xx + zz;
    const Izz = xx + yy;

    const Ixy = -xy;
    const Iyz = -yz;
    const Izx = -zx;

    if (!A) {
      A = new Matrix(3, 3);
    }

    //NOTE: Clamp to zero is used here to zero out terms that are infinitesimally small
    //and result in catastrophic errors with the Eigen decomposition.
    //Such terms occur naturally for meshes that are very symmetric and axis-aligned already
    //but have very slight numeric difference between vertex coordinates that are enough
    //to result in non-zero epsilon-like value when subtracted from each other.
    //
    //One example is the moments computed for a Revit column (basically a rectangular parallelopiped with square base):
    //[[986.2658403229639,       -2.3283064365386963e-10, -7.275957614183426e-12],
    // [-2.3283064365386963e-10, 986.2658403231967,       1.4551915228366852e-11],
    // [-7.275957614183426e-12,  1.4551915228366852e-11,  110.11534966086037]]
    //
    //Without clamping this will yield the Eigen decomposition:
    //[[ 8.50548090e-01  5.25897277e-01  8.30446104e-15]
    // [ 5.25897277e-01 -8.50548090e-01 -1.66036022e-14]
    // [ 1.67271064e-15 -1.84963534e-14  1.00000000e+00]]
    //With clamping the result is trivially:
    // [[1,0,0][0,1,0][0,0,1]]
    let invAverageAxisMoments = 3.0 / (Ixx + Iyy + Izz);
    A.rows[0][0] = clampToZero(Ixx, invAverageAxisMoments);
    A.rows[1][1] = clampToZero(Iyy, invAverageAxisMoments);
    A.rows[2][2] = clampToZero(Izz, invAverageAxisMoments);
    A.rows[1][0] = A.rows[0][1] = clampToZero(Ixy, invAverageAxisMoments);
    A.rows[2][0] = A.rows[0][2] = clampToZero(Izx, invAverageAxisMoments);
    A.rows[2][1] = A.rows[1][2] = clampToZero(Iyz, invAverageAxisMoments);
  }

  getPrincipalAxis(axis) {

    //Get the inertia tensor
    this.getCovMatrix(A);

    //Find the principal axes
    eigen_decomposition(A, V, d);

    //Use the axis of the largest eignvalue
    sort_vals(V.rows, d);

    if (!axis) {
      axis = new THREE.Vector3();
    }
    return axis.set(V.rows[0][0], V.rows[1][0], V.rows[2][0]);
  }

}